import Vue from 'vue'
import Vuex from 'vuex'
import createPersistedState from 'vuex-persistedstate'
import { securedAxiosInstance } from '@/backend/axios'
import { SearchDataModel } from '@/model/searchDataModel'
import _ from 'lodash'
import contracts from './modules/contracts'
import settings from './modules/settings'

Vue.use(Vuex)

export const omitEmptyChildren = (objects) => {
  return objects.map(object => {
    if (_.has(object, 'children') && !_.isEmpty(object.children)) {
      return _.set(object, 'children', omitEmptyChildren(object.children))
    } else {
      return _.omit(object, 'children')
    }
  })
}

const defaultState = {
  currentUser: {},
  membersSearchCondition: {},
  masters: {
    genders: [],
    educationalBackgrounds: [],
    educationalDepartments: [],
    prefectures: [],
    occupations: [],
    skills: [],
    licenses: [],
    specialFavors: [],
    businesses: [],
    employmentStatuses: [],
    evaluations: [],
    languages: [],
    languageSkills: [],
    interviewContents: [],
    optionalPermissions: [],
    receivableMailTypes: [],
    workStyles: [],
    remindStatuses: [],
    seminarStatuses: [],
    prIcons: []
  },
  isExistsSeminar: false,
  expandsSidebar: true
}

export const store = new Vuex.Store({
  state: defaultState,
  getters: {
    currentUser (state) {
      return state.currentUser
    },
    showScout (state) {
      return _.get(state.currentUser, 'permissions.isViewScout')
    },
    isSendScout (state) {
      return _.get(state.currentUser, 'permissions.isSendScout')
    },
    showAccounts (state) {
      return _.get(state.currentUser, 'permissions.isViewAccount')
    },
    isEditAccount (state) {
      return _.get(state.currentUser, 'permissions.isEditAccount')
    },
    showEntry (state) {
      return _.get(state.currentUser, 'permissions.isViewEntryUser') || _.get(state.currentUser, 'permissions.isOutsourceeViewEntryUser')
    },
    showAllowedIpAddress (state) {
      return _.get(state.currentUser, 'permissions.isEditAllowedIpAddress')
    },
    canPdfDownload (state) {
      return _.get(state.currentUser, 'permissions.isViewEntryUser')
    },
    canSendMessage (state) {
      return _.get(state.currentUser, 'permissions.isSendMessageEntryUser')
    },
    showSettingsSelection (state) {
      return _.get(state.currentUser, 'permissions.isEditSettingSelectionPhase')
    },
    isExistsSeminar (state) {
      return state.isExistsSeminar
    },
    membersSearchCondition: (state) => (jobId) => {
      return _.defaultTo(state.membersSearchCondition[jobId], new SearchDataModel())
    },
    genderMaster (state) {
      return state.masters.genders
    },
    educationalBackgroundMaster (state) {
      return state.masters.educationalBackgrounds
    },
    educationalDepartmentMaster (state) {
      return state.masters.educationalDepartments
    },
    prefecturesMaster (state) {
      return state.masters.prefectures
    },
    prefecturesOnlyMaster (state) {
      // 市区町村を抜いた地方名と件名のリストを返す
      return _.cloneDeep(state.masters.prefectures).map(area => {
        area.children.forEach(prefecture => delete prefecture.children)
        return area
      })
    },
    occupationsMaster (state) {
      return state.masters.occupations
    },
    skillsMaster (state) {
      return state.masters.skills
    },
    hasChildCategorySkillBy: (state) => (id) => {
      // 小カテゴリが1つしか無い かつ 小カテゴリ名が大カテゴリ名と同一 の場合は、小カテゴリを表示しない
      return !state.masters.skills
        .filter(skill => {
          return skill.children.length === 1 &&
          skill.name === skill.children[0].name
        })
        .map(o => o.id)
        .includes(id)
    },
    licensesMaster (state) {
      return state.masters.licenses
    },
    specialFavorsMaster (state) {
      return state.masters.specialFavors
    },
    specialFavorBy: (state) => (id) => {
      return state.masters.specialFavors.find(specialFavor => specialFavor.id === Number(id))
    },
    convertLabelById: (state) => (id, mastersKey) => {
      const target = state.masters[mastersKey].find(m => m.id === Number(id))
      return target ? target.name : ''
    },
    convertLabelForLv2ById: (state) => (id, mastersKey) => {
      const master = state.masters[mastersKey]
      const target = _(master).flatMap('children').find(m => m.id === id)
      return _.defaultTo(target.name, '')
    },
    employmentStatusMaster (state) {
      return state.masters.employmentStatuses
    },
    languageSkillsMaster (state) {
      return state.masters.languageSkills
    },
    languagesMaster (state) {
      return state.masters.languages
    },
    businessesMaster (state) {
      return state.masters.businesses
    },
    evaluationMaster (state) {
      return state.masters.evaluations
    },
    interviewContentsMaster (state) {
      return state.masters.interviewContents
    },
    optionalPermissionsMaster (state) {
      return state.masters.optionalPermissions
    },
    receivableMailTypesMaster (state) {
      return state.masters.receivableMailTypes
    },
    workStylesMaster (state) {
      return state.masters.workStyles
    },
    remindStatusesMaster (state) {
      return state.masters.remindStatuses
    },
    expandsSidebar (state) {
      return state.expandsSidebar
    },
    seminarStatuses (state) {
      return state.masters.seminarStatuses
    },
    prIconsMaster (state) {
      return state.masters.prIcons
    }
  },
  mutations: {
    setCurrentUser (state, { currentUser }) {
      state.currentUser = currentUser
    },
    setMembersSearchCondition (state, { jobId, membersSearchCondition }) {
      Vue.set(state.membersSearchCondition, jobId, new SearchDataModel(membersSearchCondition))
    },
    deleteMembersSearchConditionByJobId (state, { jobId }) {
      Vue.set(state, 'membersSearchCondition', _.omit(state.membersSearchCondition, [jobId]))
    },
    setMaster (state, { masterKey, data }) {
      Vue.set(state.masters, masterKey, data)
    },
    setExpandsSidebar (state, val) {
      state.expandsSidebar = val
    },
    setIsExistsSeminar (state, val) {
      state.isExistsSeminar = val
    },
    clearMaster (state) {
      Vue.set(state, 'masters', {
        genders: [],
        educationalBackgrounds: [],
        educationalDepartments: [],
        prefectures: [],
        occupations: [],
        skills: [],
        licenses: [],
        specialFavors: [],
        businesses: [],
        employmentStatuses: [],
        evaluations: [],
        languages: [],
        languageSkills: [],
        interviewContents: [],
        optionalPermissions: [],
        receivableMailTypes: [],
        workStyles: [],
        remindStatuses: [],
        seminarStatuses: [],
        prIcons: []
      })
    },
    clearUser (state) {
      Vue.set(state, 'currentUser', {})
    },
    clearSearchCondition (state) {
      Vue.set(state, 'membersSearchCondition', {})
    },
    clearVuex (state) {
      Object.assign(state, defaultState)
    }
  },
  actions: {
    storeMaster ({ commit, state }, masterKey) {
      if (state.masters[masterKey].length === 0) {
        return securedAxiosInstance.get(`/masters/${_.snakeCase(masterKey)}`)
          .then(response => {
            commit('setMaster', { masterKey: masterKey, data: omitEmptyChildren(response.data) })
          })
      }
    },
    setMembersSearchCondition ({ commit }, { jobId, membersSearchCondition }) {
      commit('setMembersSearchCondition', { jobId, membersSearchCondition })
    },
    deleteMembersSearchConditionByJobId ({ commit }, { jobId }) {
      commit('deleteMembersSearchConditionByJobId', { jobId })
    },
    setCurrentUser ({ commit }, { currentUser }) {
      commit('setCurrentUser', { currentUser: currentUser })
    },
    setIsExistsSeminar ({ commit }, isExistsSeminar) {
      commit('setIsExistsSeminar', isExistsSeminar)
    },
    toggleExpandsSidebar ({ commit, state }) {
      commit('setExpandsSidebar', !state.expandsSidebar)
    },
    clearMaster ({ commit }) {
      commit('clearMaster')
    },
    clearUser ({ commit }) {
      commit('clearUser')
    },
    clearSearchCondition ({ commit }) {
      commit('clearSearchCondition')
    },
    clearVuex ({ commit }) {
      commit('clearVuex')
    }
  },
  modules: {
    contracts,
    settings
  },
  plugins: [createPersistedState({ storage: window.sessionStorage })]
})
